import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Table } from 'antd'
import { SearchOutlined, ShoppingOutlined, WarningOutlined } from '@ant-design/icons'
import { Container } from 'typescript-ioc'
import { useDataViewReducer } from '@pushly/aqe/lib/components/data-view/reducer'
import { DataView } from '@pushly/aqe/lib/components/data-view/data-view'
import { DataViewContext } from '@pushly/aqe/lib/components/data-view/context'
import { DataSourceResponse } from '@pushly/aqe/lib/components/data-view/types'
import { CatalogService } from '../../services/catalog.service'
import { CatalogItemResponse, CatalogItemType } from './types'
import { getColumnsForItemType } from './columns'
import { LocaleSelector } from './locale-selector'

export interface CatalogDataViewProps {
    domainId: number
    itemType: CatalogItemType
    locale?: string
    onLocaleChange?: (locale: string) => void
    catalogLocales?: string[]
}

/**
 * CatalogDataView component for displaying catalog items in a paginated, searchable table.
 * Uses the DataView pattern from @pushly/aqe for state management and data fetching.
 */
export const CatalogDataView: React.FC<CatalogDataViewProps> = ({
    domainId,
    itemType,
    locale,
    onLocaleChange,
    catalogLocales,
}) => {
    const catalogService = Container.get(CatalogService)
    const [hasError, setHasError] = useState(false)

    // Use a ref so the dataSourceLoader closure always reads the current locale
    // without needing to recreate the callback (which the DataView wouldn't detect anyway).
    const localeRef = useRef(locale)
    localeRef.current = locale

    const [context, dispatch, requestDataSourceReload] = useDataViewReducer<CatalogItemResponse>({
        id: `catalog-${itemType}`,
        levelResource: {
            level: 'domain',
            id: domainId,
        },
        defaultFilters: {},
    })

    const itemTypeLabel = itemType.charAt(0).toUpperCase() + itemType.slice(1)
    const columns = getColumnsForItemType(itemType)

    /**
     * Data source loader for fetching catalog items.
     * Handles search filtering and pagination.
     */
    const dataSourceLoader = useCallback(
        async (
            filters: Record<string, any>,
            tableState: { pagination: { current?: number; pageSize?: number } },
        ): Promise<DataSourceResponse<CatalogItemResponse>> => {
            const query: Record<string, any> = {}

            if (localeRef.current) {
                query.locale = localeRef.current
            }

            if (filters.search) {
                query.search = filters.search
            }

            if (tableState.pagination.current) {
                query.page = tableState.pagination.current
            }

            if (tableState.pagination.pageSize) {
                query.limit = tableState.pagination.pageSize
            }

            const response = await catalogService.fetchCatalogItems(domainId, {
                query,
                cancellationKey: 'catalog-data-view.fetch',
            })

            if (response.cancelled) {
                return {
                    ok: false,
                    data: [],
                    cancelled: true,
                }
            }

            if (!response.ok) {
                setHasError(true)
                return {
                    ok: false,
                    data: [],
                }
            }

            setHasError(false)
            return {
                ok: response.ok,
                data: response.data ?? [],
                meta: response.meta,
            }
        },
        [domainId, catalogService],
    )

    // Trigger a data reload when locale changes
    const prevLocaleRef = useRef(locale)
    useEffect(() => {
        if (prevLocaleRef.current !== locale && locale !== undefined) {
            prevLocaleRef.current = locale
            requestDataSourceReload()
        }
    }, [locale, requestDataSourceReload])

    const getEmptyStateIcon = (): React.ReactNode => {
        if (hasError) {
            return (
                <>
                    <WarningOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />
                    <br />
                    <Button type="primary" onClick={handleRetry} style={{ marginTop: 8 }}>
                        Retry
                    </Button>
                </>
            )
        }
        const hasSearchTerm = context.filterValues.search && context.filterValues.search.length > 0
        if (hasSearchTerm) {
            return <SearchOutlined style={{ fontSize: 32, color: '#999' }} />
        }
        return <ShoppingOutlined style={{ fontSize: 32, color: '#999' }} />
    }

    const getEmptyStateText = (): string => {
        if (hasError) {
            return "We couldn't load your catalog. Please try again."
        }
        const hasSearchTerm = context.filterValues.search && context.filterValues.search.length > 0
        if (hasSearchTerm) {
            return `No ${itemTypeLabel}s match your search. Clear search to see all items.`
        }
        return 'Your catalog is empty – contact your account manager for more information.'
    }

    const handleRetry = () => {
        setHasError(false)
        requestDataSourceReload()
    }

    const showLocaleSelector = !!catalogLocales && catalogLocales.length > 1 && !!locale && !!onLocaleChange

    return (
        <DataViewContext.Provider value={{ state: context, dispatch }}>
            <DataView<CatalogItemResponse>
                dataSourceLoader={dataSourceLoader}
                hideAutoRefreshOptions={true}
                filtersAddonAfter={
                    showLocaleSelector ? (
                        <LocaleSelector
                            locales={catalogLocales}
                            selectedLocale={locale}
                            onLocaleChange={onLocaleChange}
                        />
                    ) : undefined
                }
                filters={[
                    {
                        key: 'search',
                        type: 'search',
                        componentProps: {
                            className: 'catalog-list-search',
                            placeholder: `Search by name, URL, or other ${itemType} property.`,
                            style: { width: 400 },
                        },
                    },
                ]}
                table={{
                    rowKey: (row) => row.item_id,
                    noData: {
                        icon: getEmptyStateIcon(),
                        text: getEmptyStateText(),
                    },
                }}
            >
                {columns.map((column) => (
                    <Table.Column<CatalogItemResponse>
                        key={column.key}
                        dataIndex={column.dataIndex}
                        title={column.title}
                        width={column.width}
                        ellipsis={column.ellipsis}
                        render={column.render}
                    />
                ))}
            </DataView>
        </DataViewContext.Provider>
    )
}
